<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="deep-gl" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("INCOTERMS.TITLE") }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div class="buttons-tab">
        <v-btn
          v-if="hasPermissions('incoterms-create')"
          class="mr-4 warning"
          :disabled="isProcessing"
          @click="handleCreateClick"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="incoterms-table"
              height="500"
              name="incoterms-table"
              :headers="headers"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="{
                method: tableItemsMethod,
              }"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
            >
              <template
                v-for="(header, index) in headers"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <span
                  v-if="header.value === 'options'"
                  :key="index"
                  class="d-flex align-center justify-center flex-nowrap"
                >
                  <v-btn
                    v-if="hasPermissions('incoterms-edit')"
                    icon
                    color="primary"
                    @click="handleEditClick(item.item.id)"
                  >
                    <v-icon dark>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="hasPermissions('incoterms-delete')"
                    icon
                    color="red"
                    @click="handleDeleteClick(item.item.id)"
                  >
                    <v-icon dark>mdi-trash-can</v-icon>
                  </v-btn>
                </span>
                <template v-else-if="header.value === 'include_transport'">
                  <v-chip
                    :key="index"
                    x-small
                    :color="item.value ? 'primary' : 'grey'"
                    dark
                  >
                    {{
                      item.value
                        ? $t("INCOTERMS.YES")
                        : $t("INCOTERMS.NO")
                    }}
                  </v-chip>
                </template>
                <template v-else>
                  {{ item.value || "-" }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <Confirm
      v-model="deleteConfirm"
      :content="$t('GENERAL.DELETE_CONFIRM')"
      @confirm="deleteIncoterm"
      @decline="deleteConfirm = false"
    />
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import Confirm from "@/views/partials/dialogs/Confirm.vue";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";
import {
  GET_INCOTERMS,
  DELETE_INCOTERM,
} from "@/store/incoterms.module";

export default {
  name: "ListIncoterms",
  components: {
    Table,
    Confirm,
  },
  data() {
    return {
      tableName: "incoterms-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      tableItemsMethod: GET_INCOTERMS,
      deleteConfirm: false,
      incotermForDelete: null,
      headers: [
        {
          text: "#",
          value: "id",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("GENERAL.LABELS.NAME"),
          value: "name",
          align: "center",
          width: "30%",
        },
        {
          text: this.$t("INCOTERMS.CODE"),
          value: "code",
          align: "center",
          width: "20%",
        },
        {
          text: this.$t("INCOTERMS.INCLUDE_TRANSPORT"),
          value: "include_transport",
          align: "center",
          width: "20%",
        },
        {
          text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"),
          value: "options",
          sortable: false,
          align: "center",
          width: "20%",
        },
      ],
    };
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );
    this.userStatePrepared = true;
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    handleCreateClick() {
      this.$router.push({ name: "incoterms-create" });
    },
    handleEditClick(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );

      this.$router.push({ name: "incoterms-edit", params: { id } });
    },
    handleDeleteClick(id) {
      this.incotermForDelete = id;
      this.deleteConfirm = true;
    },
    deleteIncoterm() {
      this.isProcessing = true;

      this.$store
        .dispatch(DELETE_INCOTERM, this.incotermForDelete)
        .then(() => {
          this.$refs["incoterms-table"].fetchServerData();
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            type: "error",
            message: this.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.isProcessing = false;
          this.deleteConfirm = false;
        });
    },
  },
};
</script>
